import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAtom, useSetAtom } from "jotai";
import { useState } from "react";
import Cookies from "js-cookie";

import {
  queriesEnabledAtom,
  setCloudLogoAtom,
  tenantAtom,
  userRecordActivityAtom,
} from "../../../atoms/GlobalAtoms";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import TenantAPI from "../../api/TenantAPI";
import API from "../../api/API";

interface UserTenantObj {
  TenantName: string;
  TenantID: string;
  TenantOwner: boolean;
  PermissionsID: string;
  Deleted?: boolean;
}

interface Props {
  userTenant: UserTenantObj;
  isDrawer?: boolean;
  onClose?: () => void;
}

/**
 * useLaunchCloud - Used to launch initial cloud, and switch between clouds.
 */
const useLaunchCloud = () => {
  const navigate = useNavigate();
  const [launching, setLaunching] = useState<string | null>(null); // The ID of the cloud that is being launched. (if any)
  const [currentTenant, setCurrentTenant] = useAtom(tenantAtom);
  const [queriesEnabled, setQueriesEnabled] = useAtom(queriesEnabledAtom);
  const setWorkspaceID = useSetAtom(workspaceIdAtom);
  const queryClient = useQueryClient();
  const setUserRecordActivity = useSetAtom(userRecordActivityAtom);
  const setCloudLogo = useSetAtom(setCloudLogoAtom);

  const launchCloud = useMutation({
    mutationFn: async ({ userTenant, isDrawer = false, onClose }: Props) => {
      const currentPath = window.location.pathname;

      const currentID = currentTenant?.TenantID;
      const newID = userTenant?.TenantID;

      // Don't do anything if the user clicked on the same cloud.
      if (
        currentID === newID &&
        isDrawer &&
        currentPath?.indexOf("/clouds") === 0
      ) {
        callFunctionIfExists(onClose);
        navigate("/clouds/" + newID + "/activity");
        return Promise.resolve(null);
      }

      sessionStorage.setItem("LastVisitedTenant", newID);
      setLaunching(newID);

      return TenantAPI.getAuthenticationToken(newID);
    },
    onSuccess: (data, { userTenant, onClose = null }) => {
      if (!data) return;

      setUserRecordActivity(false);

      const newID = userTenant?.TenantID;
      const cloudLogo: string = data.data.data?.logo?.PublicURL ?? "";
      setCloudLogo({ tenantID: newID, value: cloudLogo });

      Cookies.set(
        `authenticationToken_${newID}`,
        data.data.data.authenticationToken,
        {
          expires: 7,
          sameSite: "strict",
          secure: window.location.origin.startsWith("https"),
        }
      );

      localStorage.removeItem("workspaceID");
      setWorkspaceID(null);
      setCurrentTenant(userTenant);

      if (queriesEnabled === false) {
        setQueriesEnabled(true);
      }

      // Remove queries that possibly have conflicting query keys across clouds
      // PageIds can conflict due to global apps
      // Records can conflict with modules containing the same API name.
      // ViewIds can conflict due to global apps
      queryClient.removeQueries({
        queryKey: ["page"],
      });
      queryClient.removeQueries({
        queryKey: [currentTenant?.TenantID, "records"],
      });
      queryClient.removeQueries({
        queryKey: ["view"],
      });

      navigate("/clouds/" + newID + "/activity");
      callFunctionIfExists(onClose);
    },
    onError: async (err: any, variables) => {
      if (
        err?.response?.status === 401 &&
        err?.config?.url?.startsWith?.(window.GC_API_URL)
      ) {
        const newID = variables.userTenant?.TenantID;
        // 2 - Refresh Auth Token.
        const { authenticationToken, cloudToken } = await API.refreshToken(
          newID
        ).then((data) => data.data.data);

        Cookies.set("authenticationToken", authenticationToken, {
          expires: 7,
          sameSite: "strict",
          secure: window.location.origin.startsWith("https"),
        });

        cloudToken &&
          Cookies.set(`authenticationToken_${newID}`, cloudToken, {
            expires: 7,
            sameSite: "strict",
            secure: window.location.origin.startsWith("https"),
          });

        if (!authenticationToken) return navigate("/login");
        else {
          launchCloud.mutate(variables);
        }
      }
    },
    onSettled: () => {
      setLaunching(null);
    },
  });

  return {
    launchCloud: launchCloud.mutate,
    launching: launching,
  };
};

export default useLaunchCloud;
